import { useCallback } from 'react'

export interface PopoverPosition {
  x: number
  y: number
}

export interface PopoverDimensions {
  width: number
  height: number
}

export interface ViewportConstraints {
  margin?: number
}

export interface PositionedPopover {
  x: number
  y: number
}

/**
 * Custom hook for positioning popovers within viewport constraints
 * @param defaultDimensions - Default width and height for the popover
 * @param constraints - Viewport constraints (margin from edges)
 */
export const usePopoverPositioning = (
  defaultDimensions: PopoverDimensions,
  constraints: ViewportConstraints = { margin: 10 },
) => {
  const calculatePosition = useCallback(
    (
      targetPosition: PopoverPosition,
      popoverDimensions: PopoverDimensions = defaultDimensions,
    ): PositionedPopover => {
      const { x, y } = targetPosition
      const { width: popoverWidth, height: popoverHeight } = popoverDimensions
      const margin = constraints.margin || 10

      // Get viewport dimensions
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Default position is below the target
      // We're assuming the target position is the bottom-left corner of the target element
      let posX = x
      let posY = y + 5 // Add a small offset to position below the target
      let positionedAbove = false

      // Adjust horizontal position if needed
      if (posX + popoverWidth > viewportWidth - margin) {
        posX = viewportWidth - popoverWidth - margin
      }
      if (posX < margin) {
        posX = margin
      }

      // Check if there's enough space below
      if (posY + popoverHeight > viewportHeight - margin) {
        // Not enough space below, try positioning above
        posY = y - popoverHeight - 5 // Position above with small offset
        positionedAbove = true

        // If still not enough space above
        if (posY < margin) {
          // Position at the bottom of the viewport as last resort
          posY = viewportHeight - popoverHeight - margin
          positionedAbove = false
        }
      }

      // Add extra spacing (30px) when positioned above the tag icon
      if (positionedAbove) {
        posY -= 30
      }

      return { x: posX, y: posY }
    },
    [defaultDimensions, constraints],
  )

  const applyPosition = useCallback(
    (
      element: HTMLElement,
      targetPosition: PopoverPosition,
      popoverDimensions: PopoverDimensions = defaultDimensions,
    ) => {
      const { x, y } = calculatePosition(targetPosition, popoverDimensions)

      element.style.position = 'fixed'
      element.style.left = `${x}px`
      element.style.top = `${y}px`
    },
    [calculatePosition, defaultDimensions],
  )

  return {
    calculatePosition,
    applyPosition,
  }
}
